import React, { useEffect, useMemo, useState } from 'react';
import { Box, getDuration, useDebounce } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent, useMultipleEmployerSelectAvailable } from '../hooks';
import MultipleEmployerSelector from '../MultipleEmployerSelector';
import PlaceholderBase from '../PlaceholderBase';
import PlanSelector from '../PlanSelector';
import PlanYearSelector from '../PlanYearSelector';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import { ReportSummaryBase, ReportSummaryWrapper } from '../ReportSummary';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import { ParticipantAccountActivitySummaryReportParams } from './ParticipantActivityReport.types';
import {
  useParticipantAccountActivityReportExportSummaryCSVQuery,
  useParticipantAccountActivityReportSummaryQuery,
} from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: ParticipantAccountActivitySummaryReportParams;
  onSetFilterValue: FilterSetters<ParticipantAccountActivitySummaryReportParams>;
}

const ParticipantActivityReportSummary: React.FC<Props> = ({
  isPreviewMode,
  filtersValues,
  onSetFilterValue,
  defaultEmployerId,
}) => {
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();
  const [isAutoFocus, setAutoFocus] = useState(false);
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues, {
    isMultipleEmployerMode: true,
  });
  const debouncedPlanIds = useDebounce(filtersValues.planIds, getDuration({ seconds: 2 }));
  const {
    isLoading,
    data,
    planOptions,
    lastTimeUpdateDate,
    isEmptyData,
    isEmptyPlanOptions,
    isLoadingMore,
    isHasNextPage,
    handleLoadMore,
  } = useParticipantAccountActivityReportSummaryQuery({
    employerId: filtersValues.employerId,
    employerIds: filtersValues.employerIds,
    planYearPeriod: filtersValues.planYearPeriod,
    accountTypes: filtersValues.accountTypes,
    planIds: debouncedPlanIds,
    isMultipleEmployer: isMultipleEmployerAvailable,
  });

  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useParticipantAccountActivityReportExportSummaryCSVQuery({
      employerId: filtersValues.employerId,
      isMultipleEmployer: isMultipleEmployerAvailable,
      employerIds: filtersValues.employerIds,
      planYearPeriod: filtersValues.planYearPeriod,
      accountTypes: filtersValues.accountTypes,
      planIds: debouncedPlanIds,
    });
  useEffect(() => {
    onSetFilterValue.planIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersValues.employerId]);
  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }
    if (!isLoading && !data.length) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }

    return (
      <ReportSummaryBase
        isPreviewMode={isPreviewMode}
        isLoading={isLoading}
        data={data}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        isLoadingMore={isLoadingMore}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtersValues.employerId, filtersValues.employerIds, isLoading, isPreviewMode]);
  return (
    <TopButtonPortalWrapper
      isPreviewMode={isPreviewMode}
      infoNode={
        <ReportLastUpdateDate
          isLoading={isLoading}
          key={`${filtersValues.employerId}`}
          value={lastTimeUpdateDate}
        />
      }
      buttonNode={
        <Box direction="row">
          <AddScheduledReportButton
            employerId={filtersValues.employerId}
            employerIds={filtersValues.employerIds}
            isMultipleEmployer={isMultipleEmployerAvailable}
            type={ReportTypeKeys.ParticipantActivity}
            tab={ReportTab.Summary}
          />
          <Box margin={{ left: 'spacing12' }}>
            <ExportButton
              onClick={handleExportCSV}
              isLoading={isExportLoading}
              disabled={!isEmployerIsPresent || isEmptyData}
              text="Download Summary (CSV)"
            />
          </Box>
        </Box>
      }
    >
      <ReportSummaryWrapper
        isPreviewMode={isPreviewMode}
        filters={
          <Box fill direction="row">
            <Box fill>
              {isMultipleEmployerAvailable ? (
                <MultipleEmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  values={filtersValues.employerIds}
                  onChangeValues={onSetFilterValue.employerIds}
                />
              ) : (
                <EmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  value={filtersValues.employerId}
                  onChangeValue={onSetFilterValue.employerId}
                />
              )}
            </Box>
            <Box fill margin={{ left: 'spacing12' }}>
              <PlanYearSelector
                disabled={
                  !isEmployerIsPresent ||
                  (!!isMultipleEmployerAvailable && filtersValues.employerIds?.length !== 1)
                }
                value={filtersValues.planYearPeriod}
                onChangeValue={onSetFilterValue.planYearPeriod}
              />
            </Box>
            {!isMultipleEmployerAvailable ? (
              <Box fill margin={{ left: 'spacing12' }}>
                <PlanSelector
                  values={filtersValues.planIds}
                  options={planOptions}
                  disabled={
                    !isEmployerIsPresent ||
                    !planOptions.length ||
                    isEmptyPlanOptions ||
                    (!!isMultipleEmployerAvailable && filtersValues.employerIds?.length !== 1)
                  }
                  onChangeValues={onSetFilterValue.planIds}
                />
              </Box>
            ) : (
              <Box fill margin={{ left: 'spacing12' }} />
            )}
          </Box>
        }
      >
        {childrenNode}
      </ReportSummaryWrapper>
    </TopButtonPortalWrapper>
  );
};

export default ParticipantActivityReportSummary;
